<template>
  <div class="space-y-6">
    <!-- Header with user email and back button -->
    <page-header :title="`Login Attempts: ${email}`">
      <div class="flex items-center gap-x-4">
        <button
          class="btn-outline-primary h-10 flex"
          @click="goBack"
        >
          <ArrowLeftIcon class="size-5 -ml-1 mr-2" />
          Back to List
        </button>
        <!-- Action buttons -->
        <div v-if="userDetails" class="flex items-center gap-x-2">
          <button
            v-if="userDetails.blockStatus.isBlocked"
            class="btn-primary h-10"
            @click="onUnblockUser"
          >
            <LockOpenIcon class="size-5 -ml-1 mr-2" />
            Unblock User
          </button>
          <button
            v-else
            class="btn-primary h-10"
            @click="onBlockUser"
          >
            <LockClosedIcon class="size-5 -ml-1 mr-2" />
            Block User
          </button>
          <button
            :disabled="userDetails.blockStatus.attemptCount === 0"
            class="btn-primary h-10"
            @click="onResetAttempts"
          >
            <ArrowPathIcon class="size-5 -ml-1 mr-2" />
            Reset Counter
          </button>
          <button
            class="bg-red-500 btn text-white h-10"
            @click="onDeleteHistory"
          >
            <TrashIcon class="size-5 -ml-1 mr-2" />
            Delete History
          </button>
        </div>
      </div>
    </page-header>

    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-700">
        Failed to load user details: {{ error }}
      </p>
    </div>

    <!-- Main content -->
    <template v-else-if="userDetails">
      <!-- Current Status Card -->
      <div class="bg-white shadow rounded-lg p-6">
        <h2 class="text-xl font-semibold text-gray-700 border-b-2 border-primary-500 pb-2 mb-4">
          Current Status
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Block Status -->
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center">
              <component
                :is="userDetails.blockStatus.isBlocked ? LockClosedIcon : LockOpenIcon"
                :class="[
                  'size-8 mr-3',
                  userDetails.blockStatus.isBlocked ? 'text-red-500' : 'text-green-500'
                ]"
              />
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-600">
                  Status
                </p>
                <p
                  :class="[
                    'text-lg font-semibold',
                    userDetails.blockStatus.isBlocked ? 'text-red-600' : 'text-green-600'
                  ]"
                >
                  {{ userDetails.blockStatus.isBlocked ? 'Blocked' : 'Active' }}
                </p>

                <!-- Blocked Until info -->
                <div v-if="userDetails.blockStatus.isBlocked && userDetails.blockStatus.blockedUntil" class="mt-2 p-2 bg-red-50 rounded border border-red-200">
                  <p class="text-xs font-medium text-red-700">
                    Blocked until:
                  </p>
                  <p class="text-sm font-semibold text-red-800">
                    {{ formatDate(userDetails.blockStatus.blockedUntil) }}
                  </p>
                  <p class="text-xs text-red-600 mt-1">
                    {{ getTimeRemaining(userDetails.blockStatus.blockedUntil) }}
                  </p>
                </div>

                <!-- Active status additional info -->
                <div v-else-if="!userDetails.blockStatus.isBlocked && userDetails.blockStatus.attemptCount > 0" class="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                  <p class="text-xs text-yellow-700">
                    {{ 5 - userDetails.blockStatus.attemptCount }} attempts remaining before block
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Attempt Count -->
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center">
              <ExclamationTriangleIcon class="size-8 mr-3 text-orange-500" />
              <div>
                <p class="text-sm font-medium text-gray-600">
                  Failed Attempts
                </p>
                <p class="text-lg font-semibold text-orange-600">
                  {{ userDetails.blockStatus.attemptCount }} / 5
                </p>
              </div>
            </div>
            <div class="mt-2">
              <div class="bg-gray-200 rounded-full h-2">
                <div
                  class="bg-orange-500 h-2 rounded-full transition-all"
                  :style="`width: ${(userDetails.blockStatus.attemptCount / 5) * 100}%`"
                />
              </div>
            </div>
          </div>

          <!-- First & Last Attempt -->
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center">
              <CalendarIcon class="size-8 mr-3 text-blue-500" />
              <div>
                <p class="text-sm font-medium text-gray-600">
                  First Attempt
                </p>
                <p class="text-sm font-semibold text-gray-800">
                  {{ userDetails.firstAttempt ? formatDate(userDetails.firstAttempt) : 'Never' }}
                </p>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center">
              <ClockIcon class="size-8 mr-3 text-purple-500" />
              <div>
                <p class="text-sm font-medium text-gray-600">
                  Last Attempt
                </p>
                <p class="text-sm font-semibold text-gray-800">
                  {{ userDetails.lastAttempt ? formatDate(userDetails.lastAttempt) : 'Never' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="bg-white shadow rounded-lg p-6">
          <div class="flex items-center">
            <div class="shrink-0">
              <div class="size-8 bg-blue-100 rounded-md flex items-center justify-center">
                <ChartBarIcon class="size-5 text-blue-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                Total Attempts
              </p>
              <p class="text-2xl font-semibold text-gray-900">
                {{ userDetails.stats.total }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white shadow rounded-lg p-6">
          <div class="flex items-center">
            <div class="shrink-0">
              <div class="size-8 bg-green-100 rounded-md flex items-center justify-center">
                <CheckCircleIcon class="size-5 text-green-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                Successful
              </p>
              <p class="text-2xl font-semibold text-green-600">
                {{ userDetails.stats.successful }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white shadow rounded-lg p-6">
          <div class="flex items-center">
            <div class="shrink-0">
              <div class="size-8 bg-red-100 rounded-md flex items-center justify-center">
                <XCircleIcon class="size-5 text-red-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                Failed
              </p>
              <p class="text-2xl font-semibold text-red-600">
                {{ userDetails.stats.failed }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-white shadow rounded-lg p-6">
          <div class="flex items-center">
            <div class="shrink-0">
              <div class="size-8 bg-purple-100 rounded-md flex items-center justify-center">
                <TrophyIcon class="size-5 text-purple-600" />
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">
                Success Rate
              </p>
              <p class="text-2xl font-semibold text-purple-600">
                {{ userDetails.stats.successRate }}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Security Analysis -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Unique IPs -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">
            Unique IP Addresses
          </h3>
          <div v-if="userDetails.uniqueIPs && userDetails.uniqueIPs.length > 0" class="space-y-2">
            <div
              v-for="ip in userDetails.uniqueIPs.slice(0, 10)"
              :key="ip"
              class="flex items-center justify-between p-2 bg-gray-50 rounded"
            >
              <span class="font-mono text-sm">{{ ip }}</span>
              <GlobeAltIcon class="size-4 text-gray-400" />
            </div>
            <p v-if="userDetails.uniqueIPs.length > 10" class="text-sm text-gray-500 pt-2">
              And {{ userDetails.uniqueIPs.length - 10 }} more IPs...
            </p>
          </div>
          <p v-else class="text-gray-500">
            No IP addresses recorded
          </p>
        </div>

        <!-- Unique Devices -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">
            Devices & Browsers
          </h3>
          <div v-if="userDetails.uniqueUserAgents && userDetails.uniqueUserAgents.length > 0" class="space-y-2">
            <div
              v-for="(ua, index) in userDetails.uniqueUserAgents.slice(0, 5)"
              :key="index"
              class="p-2 bg-gray-50 rounded"
            >
              <p class="text-sm truncate" :title="ua">
                {{ ua }}
              </p>
            </div>
            <p v-if="userDetails.uniqueUserAgents.length > 5" class="text-sm text-gray-500 pt-2">
              And {{ userDetails.uniqueUserAgents.length - 5 }} more devices...
            </p>
          </div>
          <p v-else class="text-gray-500">
            No user agents recorded
          </p>
        </div>
      </div>

      <!-- Login Attempts History -->
      <div class="bg-white shadow rounded-lg">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-700">
            Login Attempts History
          </h3>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="attempt in paginatedAttempts"
                :key="attempt.id"
                class="hover:bg-gray-50"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(attempt.createdAt) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="[
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                      attempt.isSuccess
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    ]"
                  >
                    <component
                      :is="attempt.isSuccess ? CheckCircleIcon : XCircleIcon"
                      class="size-3 mr-1"
                    />
                    {{ attempt.isSuccess ? 'Success' : 'Failed' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                  {{ attempt.ipAddress || 'Unknown' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="userDetails.attempts && userDetails.attempts.length > attemptsPerPage" class="px-6 py-3 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing {{ ((currentPage - 1) * attemptsPerPage) + 1 }}
              to
              {{ Math.min(currentPage * attemptsPerPage, userDetails.attempts.length) }}
              of
              {{ userDetails.attempts.length }}
              attempts
            </div>
            <div class="flex items-center space-x-2">
              <button
                :disabled="currentPage <= 1"
                class="px-3 py-1 text-sm bg-white border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                @click="currentPage--"
              >
                Previous
              </button>
              <span class="text-sm text-gray-700">{{ currentPage }} / {{ totalPages }}</span>
              <button
                :disabled="currentPage >= totalPages"
                class="px-3 py-1 text-sm bg-white border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                @click="currentPage++"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useLoginAttempts } from '../composables/useLoginAttempts';
import { Modal } from '../composables/useModal';
import PageHeader from '../components/PageHeader.vue';
import { formatDate } from '../utils/formatters';

import {
  ArrowLeftIcon,
  LockOpenIcon,
  LockClosedIcon,
  ArrowPathIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrophyIcon,
  GlobeAltIcon
} from '@heroicons/vue/24/outline';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { getUserDetails, blockUser, unblockUser, resetUserAttempts, deleteUserHistory } = useLoginAttempts();

const email = ref(route.params.email);
const userDetails = ref(null);
const loading = ref(true);
const error = ref(null);
const currentPage = ref(1);
const attemptsPerPage = 20;

const totalPages = computed(() => {
  if (!userDetails.value?.attempts) return 1;
  return Math.ceil(userDetails.value.attempts.length / attemptsPerPage);
});

const paginatedAttempts = computed(() => {
  if (!userDetails.value?.attempts) return [];
  const start = (currentPage.value - 1) * attemptsPerPage;
  const end = start + attemptsPerPage;
  return userDetails.value.attempts.slice(start, end);
});

function getTimeRemaining(blockedUntil) {
  if (!blockedUntil) return '';

  const now = new Date();
  const blockEnd = new Date(blockedUntil);
  const diffMs = blockEnd.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Block expired';
  }

  const diffMinutes = Math.ceil(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  } else {
    return `${minutes}m remaining`;
  }
}

function goBack() {
  router.push('/login-attempts');
}

async function loadUserDetails() {
  try {
    loading.value = true;
    error.value = null;

    const response = await getUserDetails(email.value);

    if (response.success) {
      userDetails.value = response.userDetails;
    } else {
      error.value = 'Failed to load user details';
    }
  } catch (err) {
    console.error('Error loading user details:', err);
    error.value = err.message || 'An error occurred while loading user details';
  } finally {
    loading.value = false;
  }
}

async function onBlockUser() {
  console.log('onBlockUser called');

  const result = await Modal.confirm({
    title: 'Block User',
    body: `Block the user "${email.value}" for 20 minutes?`,
    okButton: 'Block',
    iconColor: 'danger',
    inputField: {
      id: 'blockDuration',
      label: 'Block duration (minutes):',
      type: 'number',
      defaultValue: '20',
      min: '1',
      max: '1440',
      description: 'Enter block duration in minutes (1-1440)'
    }
  });

  console.log('Modal result:', result);

  if (!result.confirmed) {
    console.log('User cancelled modal');
    return;
  }

  try {
    const blockDurationMinutes = parseInt(result.inputValue) || 20;
    console.log('Blocking user:', email.value, 'for', blockDurationMinutes, 'minutes');

    const response = await blockUser(email.value, blockDurationMinutes);
    console.log('Block response:', response);

    if (response.success) {
      toast.success(`User blocked for ${blockDurationMinutes} minutes`);
      console.log('Refreshing user details...');
      await loadUserDetails(); // Refresh data
    } else {
      console.error('Block failed:', response);
      toast.error('Failed to block user');
    }
  } catch (err) {
    console.error('Error blocking user:', err);
    toast.error('Error blocking user');
  }
}

async function onUnblockUser() {
  const confirm = await Modal.confirm({
    title: 'Unblock User',
    body: `Unblock the user "${email.value}"?`,
    okButton: 'Unblock',
    iconColor: 'success'
  });

  if (!confirm) return;

  try {
    await resetUserAttempts(email.value);
    const response = await unblockUser(email.value);

    if (response.success) {
      toast.success('User unblocked successfully');
      await loadUserDetails(); // Refresh data
    } else {
      toast.error('Failed to unblock user');
    }
  } catch (err) {
    console.error('Error unblocking user:', err);
    toast.error('Error unblocking user');
  }
}

async function onResetAttempts() {
  const confirm = await Modal.confirm({
    title: 'Reset Attempts Counter',
    body: `Reset the failed attempts counter for "${email.value}"? This will set the counter to 0.`,
    okButton: 'Reset',
    iconColor: 'warning'
  });

  if (!confirm) return;

  try {
    const response = await resetUserAttempts(email.value);

    if (response.success) {
      toast.success('Attempts counter reset successfully');
      await loadUserDetails(); // Refresh data
    } else {
      toast.error('Failed to reset attempts counter');
    }
  } catch (err) {
    console.error('Error resetting attempts:', err);
    toast.error('Error resetting attempts counter');
  }
}

async function onDeleteHistory() {
  const confirm = await Modal.confirm({
    title: 'Delete Login History',
    body: `Delete ALL login attempt history for "${email.value}"? This action cannot be undone.`,
    okButton: 'Delete History',
    iconColor: 'danger'
  });

  if (!confirm) return;

  try {
    const response = await deleteUserHistory(email.value);

    if (response.success) {
      toast.success(`Deleted ${response.deletedCount} login attempt records`);
      await loadUserDetails(); // Refresh data
    } else {
      toast.error('Failed to delete user history');
    }
  } catch (err) {
    console.error('Error deleting history:', err);
    toast.error('Error deleting user history');
  }
}

onMounted(() => {
  if (!email.value) {
    router.push('/login-attempts');
    return;
  }

  loadUserDetails();
});
</script>
